import { useTranslation } from "react-i18next";
import { SectionTitle } from "@/components/ui/SectionTitle";
import { useMsGetEventsCalendarQuery } from "@/services/(my-space)/api.events";
import { useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { EventCard } from "./events-card";
import type { ICalendarDay, ICalendarEvent } from "@/types/(my-space)/events.interface";
import { formatDate } from "@/utils/helper";
import { Spinner } from "@/components/ui/v2/spinner";
import { parseISO } from "date-fns";

export const EventsCalendar = ({ className }: { className?: string }) => {
  const { t } = useTranslation("my-space");

  const [, setSelectedDate] = useState<Date | undefined>(undefined);
  const clearSelectedDate = () => setSelectedDate(undefined);

  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const handleMonthChange = (date: Date) => {
    clearSelectedDate();
    setSelectedMonth(date);
  };

  const year = String(selectedMonth.getFullYear());
  const month = String(selectedMonth.getMonth() + 1);

  const { data: { data: events } = {}, isFetching } = useMsGetEventsCalendarQuery({
    year,
    month,
  });

  const eventDates = useMemo(() => {
    if (!events?.days) return [];
    return events.days.map((day) => parseISO(day.date));
  }, [events]);

  const modifiers = {
    hasEvent: eventDates,
  };

  return (
    <div className={`w-full bg-white h-full rounded-sm border border-surface-elevated overflow-hidden ${className}`}>
      <DayPicker
        animate
        mode="single"
        required={false}
        month={selectedMonth}
        onMonthChange={handleMonthChange}
        className="day-picker-custom"
        modifiers={modifiers}
        modifiersClassNames={{
          hasEvent:
            "relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:bg-primary after:rounded-full font-bold text-primary",
        }}
      />
      <section className="px-3 py-2 mt-4 h-full">
        <SectionTitle title={t("events.calendar_title")} size="sm" />
        <div className="space-y-3 h-full max-h-48 md:max-h-96 overflow-y-scroll scrollbar-thin">
          {isFetching ? (
            <Spinner />
          ) : !events?.days?.length ? (
            <p className="text-sm text-black/50">{t("events.no_upcoming")}</p>
          ) : (
            events.days.map((day: ICalendarDay) =>
              day.events.map((event: ICalendarEvent) => (
                <EventCard
                  key={event.id}
                  title={event.title}
                  status={event.status}
                  startDate={formatDate(event.startDateTime)}
                  endDate={formatDate(event.endDateTime)}
                />
              )),
            )
          )}
        </div>
      </section>
    </div>
  );
};
